import { usePlayerTranslations } from "@/i18n/player";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MediaPlayer,
  MediaProvider,
  Track,
  isHLSProvider,
  type MediaPlayerInstance,
  type MediaProviderAdapter,
} from "@vidstack/react";
import {
  DefaultVideoLayout,
  defaultLayoutIcons,
} from "@vidstack/react/player/layouts/default";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import {
  dominantDirection,
  findActiveIndex,
  formatTimecode,
  isSpaceless,
  msToSeconds,
  normaliseChapters,
  toSubtitleCues,
  toTranslatedSubtitleCues,
  type Chapter,
  type Utterance,
} from "@rec/shared";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useFormat } from "@/i18n/format";

/**
 * The video surface.
 *
 * Two sources, both handled here: an HLS playlist once the transcode job has
 * produced one, and the original MP4 until then. Vidstack loads hls.js for the
 * first and uses the video element directly for the second — the difference is
 * the declared type, not the component.
 *
 * Seeking behaves identically either way, so click-to-timestamp does not care
 * which is playing.
 *
 * `onTimeUpdate` fires several times a second. It writes to a ref on every tick
 * and to state only when the whole-second value changes, so the transcript
 * re-renders about once a second instead of about sixty times.
 */

/** Subtitle size, as a multiplier on Vidstack's own player-relative size. */
export type CaptionSize = "s" | "m" | "l";

/*
 * The scale for each step lives in `index.css` beside the rest of the caption
 * styling, keyed off a `rec-captions-*` class. It cannot live here: Vidstack
 * rewrites the player's inline style for its own layout variables, so a size
 * set in JavaScript never reaches the DOM.
 */

const CAPTION_SIZE_KEY = "caption-size";

/**
 * Remembered per person, per device — a reading preference, like the
 * transcript's Lines/Paragraphs. Not tenant data.
 */
export function useCaptionSize(): [CaptionSize, (size: CaptionSize) => void] {
  const [size, setSize] = useState<CaptionSize>(() => {
    const stored = localStorage.getItem(CAPTION_SIZE_KEY);
    return stored === "s" || stored === "l" ? stored : "m";
  });

  return [
    size,
    (next) => {
      localStorage.setItem(CAPTION_SIZE_KEY, next);
      setSize(next);
    },
  ];
}

/** A ready translation, keyed by utterance id, for the subtitle track. */
export type TranslatedSubtitles = {
  language: string;
  byId: Map<string, string>;
};

type RecordingPlayerProps = {
  src: string;
  mediaType: "hls" | "mp4";
  title: string;
  chapters: Chapter[];
  /** Subtitles are derived from these; omit for a recording with no transcript. */
  utterances?: Utterance[];
  /** Show subtitles in this translation instead of the transcript's language. */
  translation?: TranslatedSubtitles;
  captionSize?: CaptionSize;
  /** Seconds to start at, from the `?t=` deep link. */
  startAt?: number;
  onTimeChange: (ms: number) => void;
  playerRef: React.RefObject<MediaPlayerInstance | null>;
};

export function RecordingPlayer({
  src,
  mediaType,
  title,
  chapters,
  utterances,
  translation,
  captionSize = "m",
  startAt,
  onTimeChange,
  playerRef,
}: RecordingPlayerProps) {
  const playerTranslations = usePlayerTranslations();
  const lastWholeSecond = useRef(-1);

  /**
   * Send the session cookie with every HLS request.
   *
   * The playlists and segments are served by our API, which authenticates them
   * — and the API is a different origin from the app, so hls.js will not attach
   * credentials unless told to. Without this every manifest request is a 401
   * and the player spins with no visible cause.
   *
   * The MP4 path does not need this: it uses a presigned URL, which carries its
   * own authorisation in the query string.
   */
  const handleProviderChange = useCallback(
    (provider: MediaProviderAdapter | null) => {
      if (!provider || !isHLSProvider(provider)) return;

      provider.config = {
        ...provider.config,
        xhrSetup: (xhr: XMLHttpRequest) => {
          xhr.withCredentials = true;
        },
      };
    },
    [],
  );

  const handleTimeUpdate = useCallback(
    (detail: { currentTime: number }) => {
      const second = Math.floor(detail.currentTime);
      if (second === lastWholeSecond.current) return;
      lastWholeSecond.current = second;
      onTimeChange(Math.round(detail.currentTime * 1000));
    },
    [onTimeChange],
  );

  return (
    <MediaPlayer
      ref={playerRef}
      title={title}
      src={{
        src,
        type: mediaType === "hls" ? "application/x-mpegurl" : "video/mp4",
      }}
      currentTime={startAt}
      onTimeUpdate={handleTimeUpdate}
      onProviderChange={handleProviderChange}
      onProviderSetup={handleProviderChange}
      crossOrigin
      playsInline
      // Fetch the header and nothing else until the viewer presses play.
      // Without this the browser pulls the media eagerly, so opening a
      // recording to read its transcript downloads the whole video too.
      preload="metadata"
      /*
        Caption size rides on a class, not the `style` prop.
        
        Vidstack owns the `--media-user-*` custom properties for its own
        captions settings menu and rewrites the element's inline style, so
        anything set there is silently dropped — `--media-user-font-size` never
        reached the DOM at all, and S/M/L did nothing. `className` is forwarded
        untouched, so the size lives there and the values live in CSS.
      */
      className={cn(
        "w-full overflow-hidden rounded-xl border border-border bg-black",
        `rec-captions-${captionSize}`,
      )}
      // Chapters come from our own rows rather than a sidecar VTT file, so the
      // scrubber markers and the sidebar can never disagree.
      storage="rec-player"
    >
      <MediaProvider />
      <DefaultVideoLayout icons={defaultLayoutIcons} translations={playerTranslations} />
      {chapters.length > 0 ? <ChapterTrack chapters={chapters} /> : null}
      {utterances && utterances.length > 0 ? (
        <SubtitleTrack
          // Keyed by language so switching replaces the registered track
          // rather than mutating one Vidstack has already indexed.
          key={translation?.language ?? "original"}
          utterances={utterances}
          translation={translation}
        />
      ) : null}
    </MediaPlayer>
  );
}

/**
 * Chapter markers on the scrubber.
 *
 * Must be Vidstack's `<Track>`, not a plain `<track>` element. Vidstack creates
 * the underlying media element itself, so a raw `<track>` child is rendered into
 * the DOM and never registered as a text track — the markers and the hover title
 * silently do not appear, which is exactly how this shipped broken.
 *
 * Cues are passed as data rather than a generated VTT blob: no serialising, no
 * parsing, and no object URL to leak in a single-page app that never reloads.
 * Vidstack wants seconds, and these offsets came from real utterance timings, so
 * a marker sits exactly where the chapter starts.
 */
function ChapterTrack({ chapters }: { chapters: Chapter[] }) {
  const { t } = useTranslation("recording");
  const content = useMemo(
    () => ({
      // Normalised first: overlapping cues make the player's own title bar name
      // a chapter that ended minutes ago, because it resolves "now" against
      // whichever overlapping cue it meets first.
      cues: normaliseChapters(chapters).map((chapter) => ({
        startTime: msToSeconds(chapter.startMs),
        endTime: msToSeconds(chapter.endMs),
        text: chapter.title.replace(/\n/g, " "),
      })),
    }),
    [chapters],
  );

  return <Track content={content} type="json" kind="chapters" default label={t("chapters.trackLabel")} />;
}

/**
 * Subtitles, derived from the transcript at display time.
 *
 * No file is generated and nothing is stored: the cues are computed from the
 * utterances already loaded for the transcript panel, so every recording that
 * has a transcript has subtitles — including ones made long before this
 * existed — and a speaker correction changes the subtitles too.
 *
 * `default` is deliberately absent. Subtitles on by default cover the video for
 * everyone who did not want them, and the player remembers the choice per
 * viewer once made.
 */
function SubtitleTrack({
  utterances,
  translation,
}: {
  utterances: Utterance[];
  translation?: TranslatedSubtitles;
}) {
  const { t } = useTranslation("recording");
  const f = useFormat();
  const content = useMemo(() => {
    // A translation swaps each utterance's text by id and re-chunks; timings
    // stay derived from the words, exact at every utterance boundary.
    const cues = translation
      ? toTranslatedSubtitleCues(utterances, translation.byId, {
          spaceless: isSpaceless(translation.language),
        })
      : toSubtitleCues(utterances);

    return {
      cues: cues.map((cue) => ({
        startTime: msToSeconds(cue.startMs),
        endTime: msToSeconds(cue.endMs),
        text: cue.text,
      })),
    };
  }, [utterances, translation]);

  return (
    <Track
      content={content}
      type="json"
      kind="subtitles"
      label={translation ? f.languageName(translation.language) : t("player.transcriptTrack")}
      language={translation?.language ?? ""}
      default={Boolean(translation)}
    />
  );
}

/** Chapter list beside the player. Clicking a title seeks. */
export function ChapterList({
  chapters,
  currentMs,
  onSeek,
}: {
  chapters: Chapter[];
  currentMs: number;
  onSeek: (ms: number) => void;
}) {
  const { t } = useTranslation("recording");
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  /**
   * Follow playback, and stop the moment the reader scrolls away.
   *
   * The same contract as the transcript, because it is the same promise: the
   * list keeps up on its own while you watch, and gets out of the way the
   * instant you go looking for something else.
   */
  const [following, setFollowing] = useState(true);
  const lastScrolledId = useRef<string | null>(null);
  /**
   * Ignores the scroll events our own smooth scroll produces.
   *
   * Without it, the animation's first frames report the active chapter as still
   * off-screen, `following` flips to false, and "Jump to current" flashes up
   * during a scroll the reader did not ask for and did not make.
   */
  const selfScrollingUntil = useRef(0);

  const activeIdForScroll = useRef<string | undefined>(undefined);

  useEffect(() => {
    const id = activeIdForScroll.current;
    const container = scrollRef.current;
    const element = activeRef.current;

    if (!following || !id || !container || !element) return;
    if (lastScrolledId.current === id) return;

    lastScrolledId.current = id;
    selfScrollingUntil.current = Date.now() + 700;

    // Centred by arithmetic rather than `scrollIntoView`, which also scrolls
    // every scrollable ancestor — including the page behind the panel.
    container.scrollTo({
      top: element.offsetTop - container.clientHeight / 2 + element.clientHeight / 2,
      behavior: "smooth",
    });
  });

  const onUserScroll = useCallback(() => {
    if (Date.now() < selfScrollingUntil.current) return;

    const container = scrollRef.current;
    const element = activeRef.current;
    if (!container || !element) return;

    const view = container.getBoundingClientRect();
    const item = element.getBoundingClientRect();

    setFollowing(item.bottom > view.top && item.top < view.bottom);
  }, []);

  if (chapters.length === 0) return null;

  /**
   * The chapter playback is in, by the same rule the transcript uses.
   *
   * `find(start <= now < end)` returned the first *listed* chapter whose range
   * contained the moment, which with overlapping ranges is the earliest one —
   * so seeking to 6:20 highlighted a chapter that began at 2:50. Taking the
   * last chapter that has started is immune to both overlaps and gaps, and it
   * is what makes the transcript stay in step.
   */
  const ordered = normaliseChapters(chapters);
  const activeIndex = findActiveIndex(
    ordered.map((c) => c.startMs),
    currentMs,
  );
  const activeId = activeIndex >= 0 ? ordered[activeIndex]?.id : undefined;
  // Read by the scroll effect above, which runs before this line on the next
  // render and therefore cannot close over `activeId` directly.
  activeIdForScroll.current = activeId;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={onUserScroll}
        className="relative flex-1 space-y-1 overflow-y-auto overscroll-contain"
      >
        {ordered.map((chapter) => {
          const isActive = chapter.id === activeId;
          return (
            <button
              key={chapter.id}
              ref={isActive ? activeRef : undefined}
              type="button"
              onClick={() => onSeek(chapter.startMs)}
              className={cn(
                "group block w-full rounded-lg px-3 py-2.5 text-left transition-colors",
                isActive ? "bg-highlight" : "hover:bg-surface-hover",
              )}
            >
              <div className="flex items-baseline justify-between gap-3">
                {/*
                  A title reads as a title whether or not it is the one playing.
                  Dimming the rest made a list of nineteen chapters look like
                  nineteen disabled things, and the highlight and the timestamp
                  already say which one is current.
                */}
                <span
                  dir={dominantDirection(chapter.title)}
                  className="text-[13.5px] font-semibold text-text"
                >
                  {chapter.title}
                </span>
                <span className="shrink-0 font-mono text-[11px] tabular-nums text-text-faint">
                  {formatTimecode(chapter.startMs)}
                </span>
              </div>
              <p
                dir={dominantDirection(chapter.summary)}
                className="mt-1 line-clamp-2 text-[12.5px] leading-relaxed text-text-faint"
              >
                {chapter.summary}
              </p>
            </button>
          );
        })}
      </div>

      {/* Offer the way back rather than stealing scroll — same as the transcript. */}
      {!following && activeId ? (
        <button
          type="button"
          onClick={() => {
            setFollowing(true);
            lastScrolledId.current = null;
          }}
          className={cn(
            "absolute bottom-4 left-1/2 -translate-x-1/2",
            "rounded-full border border-border bg-surface px-3.5 py-1.5",
            "text-[13px] font-medium text-text shadow-[var(--shadow-md)]",
            "transition-colors hover:bg-surface-hover",
          )}
        >
          {t("player.jumpToCurrent")}
        </button>
      ) : null}
    </div>
  );
}

/** Seek helper shared by the transcript, chapters, and chat citations. */
export function seekTo(
  player: MediaPlayerInstance | null,
  ms: number,
  options?: { play?: boolean },
): void {
  if (!player) return;
  player.currentTime = msToSeconds(ms);
  if (options?.play) void player.play();
}
